import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import { Star, Shield, Zap, Globe, MessageSquare, Building2, User, UserPlus } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import TodayInHistory from "@/components/TodayInHistory";
import AtaturkQuote from "@/components/AtaturkQuote";
import HeroOfTheDay from "@/components/HeroOfTheDay";
import ScientistOfTheDay from "@/components/ScientistOfTheDay";
import LegendOfTheDay from "@/components/LegendOfTheDay";
import BrandOfTheDay from "@/components/BrandOfTheDay";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = '/api';

export default function HomePage() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [userType, setUserType] = useState("adult");
  const [isLogin, setIsLogin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isGuestReady, setIsGuestReady] = useState(false);
  const navigate = useNavigate();
  const { isDark } = useTheme();

  // Org login state
  const [orgCode, setOrgCode] = useState("");
  const [orgEmail, setOrgEmail] = useState("");
  const [orgPassword, setOrgPassword] = useState("");
  const [orgLoading, setOrgLoading] = useState(false);

  // Active tab
  const [activeTab, setActiveTab] = useState("bireysel");

  useEffect(() => {
    const autoGuestLogin = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('parent_alert') === 'true') {
        setIsGuestReady(true);
        return;
      }
      const token = localStorage.getItem('access_token');
      if (token) {
        setIsGuestReady(true);
        return;
      }
      try {
        const response = await axios.post(`${API}/auth/guest-login`);
        localStorage.setItem('access_token', response.data.access_token);
        localStorage.setItem('refresh_token', response.data.refresh_token);
        localStorage.setItem('user', JSON.stringify({ ...response.data.user, is_guest: true }));
        setIsGuestReady(true);
      } catch (error) {
        console.error('Auto guest login error:', error);
      }
    };
    autoGuestLogin();
  }, []);

  const handleStart = async () => {
    if (!email || !password || (!isLogin && !name)) {
      toast.error("Lütfen tüm alanları doldurun");
      return;
    }
    setLoading(true);
    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin ? { email, password } : { email, name, password, user_type: userType };
      const response = await axios.post(`${API}${endpoint}`, null, { params: payload });
      localStorage.setItem('access_token', response.data.access_token);
      localStorage.setItem('refresh_token', response.data.refresh_token);
      const userFromBackend = response.data.user;
      if (userFromBackend) {
        localStorage.setItem('user', JSON.stringify(userFromBackend));
      } else {
        const userInfo = {
          id: response.data.user_id || Math.random().toString(),
          email, name,
          subscription_status: response.data.subscription_status || 'free',
          is_parent: response.data.is_parent || false
        };
        localStorage.setItem('user', JSON.stringify(userInfo));
      }
      toast.success(isLogin ? "Giriş başarılı!" : "Kayıt başarılı!");
      if (userFromBackend) {
        const isPro = userFromBackend.subscription_status === 'pro';
        const isParent = userFromBackend.is_parent === true;
        if (isPro && isParent) navigate("/parent/dashboard");
        else navigate("/chat");
      } else {
        navigate("/chat");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || (isLogin ? "Giriş başarısız" : "Kayıt başarısız"));
    } finally {
      setLoading(false);
    }
  };

  const handleGuestLogin = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/guest-login`);
      localStorage.setItem('access_token', response.data.access_token);
      localStorage.setItem('refresh_token', response.data.refresh_token);
      const userInfo = {
        id: response.data.user.id,
        email: response.data.user.email,
        name: response.data.user.name,
        is_guest: true
      };
      localStorage.setItem('user', JSON.stringify(userInfo));
      toast.success("Misafir olarak giriş yaptınız!");
      navigate("/chat");
    } catch (error) {
      toast.error("Misafir girişi başarısız");
    } finally {
      setLoading(false);
    }
  };

  const handleOrgLogin = async () => {
    if (!orgCode || !orgEmail || !orgPassword) {
      toast.error("Lütfen tüm alanları doldurun");
      return;
    }
    setOrgLoading(true);
    try {
      const response = await axios.post(`${API}/auth/org-login`, {
        org_code: orgCode,
        email: orgEmail,
        password: orgPassword
      });
      // Store in sessionStorage for org context
      sessionStorage.setItem("orgToken", response.data.access_token);
      sessionStorage.setItem("orgUser", JSON.stringify(response.data.user));
      // Also store in localStorage for API calls
      localStorage.setItem("access_token", response.data.access_token);
      localStorage.setItem("refresh_token", response.data.refresh_token);
      toast.success("Kurumsal giriş başarılı!");
      navigate(response.data.redirect_url || "/app");
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 404) {
        toast.error("Geçersiz kurum kodu");
      } else if (error.response?.status === 403) {
        toast.error(detail || "Bu kuruma kayıtlı değilsiniz");
      } else if (error.response?.status === 401) {
        toast.error("E-posta veya şifre hatalı");
      } else {
        toast.error(detail || "Kurumsal giriş başarısız");
      }
    } finally {
      setOrgLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"></div>
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-10 left-20 text-white text-9xl">✦</div>
        <div className="absolute top-40 right-32 text-white text-7xl">❋</div>
        <div className="absolute bottom-20 left-40 text-white text-8xl">✿</div>
        <div className="absolute bottom-40 right-20 text-white text-6xl">❊</div>
      </div>
      <div className="absolute top-20 left-10 w-72 h-72 bg-red-600/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-red-500/5 rounded-full blur-3xl"></div>

      <div className="relative z-10 container mx-auto px-4 py-8">
        {/* Header */}
        <header className="text-center mb-8">
          <div className="flex items-center justify-center mb-4 gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-10 h-10 bg-gradient-to-br from-red-600 to-red-700 rounded-lg flex items-center justify-center">
              <span className="text-xl brightness-0 invert">🎓</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white tracking-tight">Zeha Academy</h1>
          </div>
          <p className="text-lg text-slate-300 mb-4">Türkiye'nin yapay zeka destekli eğitim platformu</p>

          <Button
            onClick={() => navigate('/chat')}
            size="lg"
            className="bg-red-600 text-white hover:bg-red-700 px-8 py-6 text-lg font-semibold shadow-2xl shadow-red-600/25"
          >
            <MessageSquare className="w-5 h-5 mr-2" />
            Zeha ile Sohbet Başlat
          </Button>
        </header>

        {/* Login Tabs */}
        <div className="max-w-md mx-auto mb-12">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-slate-800/50 border border-slate-700">
              <TabsTrigger value="bireysel" className="data-[state=active]:bg-red-600 data-[state=active]:text-white text-slate-300 text-xs sm:text-sm">
                <User className="w-3.5 h-3.5 mr-1.5" />
                Bireysel
              </TabsTrigger>
              <TabsTrigger value="misafir" className="data-[state=active]:bg-red-600 data-[state=active]:text-white text-slate-300 text-xs sm:text-sm">
                <UserPlus className="w-3.5 h-3.5 mr-1.5" />
                Misafir
              </TabsTrigger>
            </TabsList>

            {/* Bireysel Giriş */}
            <TabsContent value="bireysel">
              <Card className="backdrop-blur-xl bg-white/10 border-white/20">
                <CardHeader className="pb-3">
                  <CardTitle className="text-white text-lg">
                    {isLogin ? "Giriş Yap" : "Kayıt Ol"}
                  </CardTitle>
                  <CardDescription className="text-slate-300 text-sm">
                    {isLogin ? "Hesabınıza giriş yapın" : "Yeni hesap oluşturun"}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {!isLogin && (
                    <Input
                      placeholder="Adınız"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                    />
                  )}
                  <Input
                    type="email"
                    placeholder="E-posta"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                  />
                  <Input
                    type="password"
                    placeholder="Şifre"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                    onKeyDown={(e) => e.key === "Enter" && handleStart()}
                  />
                  <Button
                    onClick={handleStart}
                    disabled={loading}
                    className="w-full bg-red-600 hover:bg-red-700 text-white"
                  >
                    {loading ? "İşleniyor..." : isLogin ? "Giriş Yap" : "Kayıt Ol"}
                  </Button>
                  <button
                    onClick={() => setIsLogin(!isLogin)}
                    className="w-full text-center text-sm text-slate-400 hover:text-white transition-colors"
                  >
                    {isLogin ? "Hesabınız yok mu? Kayıt olun" : "Zaten hesabınız var mı? Giriş yapın"}
                  </button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Misafir */}
            <TabsContent value="misafir">
              <Card className="backdrop-blur-xl bg-white/10 border-white/20">
                <CardHeader className="pb-3">
                  <CardTitle className="text-white text-lg">Misafir Girişi</CardTitle>
                  <CardDescription className="text-slate-300 text-sm">
                    Kayıt olmadan hemen deneyin (20 kredi)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button
                    onClick={handleGuestLogin}
                    disabled={loading}
                    className="w-full bg-slate-700 hover:bg-slate-600 text-white"
                    size="lg"
                  >
                    {loading ? "Hazırlanıyor..." : "Misafir Olarak Başla"}
                  </Button>
                  <p className="text-xs text-slate-400 text-center mt-3">
                    Misafir hesabı ile sınırlı özellikler kullanabilirsiniz
                  </p>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
          
          {/* Kurumsal giriş linki */}
          <button
            onClick={() => navigate('/login/corporate')}
            className="w-full text-center text-sm text-slate-400 hover:text-white transition-colors mt-4 flex items-center justify-center gap-2 py-2"
          >
            <Building2 className="w-4 h-4" />
            Kurumsal Giriş ve Kayıt
          </button>
        </div>

        {/* Widgets Section */}
        {isGuestReady && (
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl font-bold text-white text-center mb-6">Bugünün İçerikleri</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              <AtaturkQuote />
              <TodayInHistory />
              <HeroOfTheDay />
              <ScientistOfTheDay />
              <LegendOfTheDay />
              <BrandOfTheDay />
            </div>
          </div>
        )}

        {/* Features Section */}
        <div className="max-w-5xl mx-auto mt-12">
          <h2 className="text-3xl font-bold text-white text-center mb-8">Zeha'nın Özellikleri</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="backdrop-blur-xl bg-white/10 border-white/20">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-red-600/20">
                    <Zap className="w-6 h-6 text-red-300" />
                  </div>
                  <CardTitle className="text-white">Güçlü AI</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-slate-300">
                  Türk kültürü ve tarihine özel olarak eğitilmiştir
                </CardDescription>
              </CardContent>
            </Card>
            <Card className="backdrop-blur-xl bg-white/10 border-white/20">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-white/20">
                    <Globe className="w-6 h-6 text-white" />
                  </div>
                  <CardTitle className="text-white">Türkçe Destek</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-slate-300">
                  Ana dili Türkçe, kültürümüze özel yanıtlar
                </CardDescription>
              </CardContent>
            </Card>
            <Card className="backdrop-blur-xl bg-white/10 border-white/20">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-white/20">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  <CardTitle className="text-white">Güvenli</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-slate-300">
                  Milli güvenlik öncelikleri ile tasarlanmış
                </CardDescription>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Institutional CTA */}
        <div className="max-w-3xl mx-auto mt-12">
          <Card className="backdrop-blur-xl bg-white/10 border-white/20">
            <CardContent className="pt-6 text-center">
              <h3 className="text-xl font-bold text-white mb-2">🏫 Okul & Kurum Çözümleri</h3>
              <p className="text-slate-300 mb-4 text-sm">
                Okulunuz için toplu lisanslama, özel admin paneli ve kurumsal fiyatlandırma
              </p>
              <Button
                onClick={() => navigate('/enterprise')}
                className="bg-white text-slate-900 hover:bg-slate-100 font-semibold"
              >
                Kurumsal Çözümler →
              </Button>
            </CardContent>
          </Card>
        </div>

        <footer className="text-center mt-16 pb-8">
          <div className="flex items-center justify-center gap-2 text-white/60 text-sm">
            <span>🇹🇷</span>
            <span>Yerli ve Milli Teknoloji</span>
            <span>🇹🇷</span>
          </div>
        </footer>
      </div>
    </div>
  );
}
